/// <amd-module name="Wit/WorkItemDataManager/CacheManagers/WorkItemIndexDBCacheManager" />
import { IVssPageContext } from "VSS/Platform/Context";
import { publishEvent } from "VSS/Platform/Telemetry";
import { traceWarning } from "VSS/Platform/Trace";
import { WITCommonConstants } from "Wit/Common/Generated/Constants";
import { constructCacheProvider } from "Wit/WorkItemDataManager/CacheManagers/CacheProviders";
import { ICacheProvider } from "Wit/WorkItemDataManager/Contracts";

const WorkItemDataTelemetryFeature = "WITData";

export class WorkItemIndexDBCacheManager {
    private _cacheProvider: ICacheProvider | undefined;
    private _pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;
    }

    public getDataFromCache<T>(scopeId: string, type: string, key: string, cacheStamp: string): Promise<T | null> {
        key = this._transformKey(key);

        // First, get current cache stamp
        if (!cacheStamp) {
            // This should not happen, nevertheless, try to continue without caching
            traceWarning(
                this._pageContext,
                WITCommonConstants.WorkItemTrackingArea,
                "IndexDbManager.GetCacheStamp",
                `Could not get cache stamp for '${type}'`
            );
        }

        return this._tryGetDataFromCache<T>(scopeId, type, key, cacheStamp)
            .catch(reason => {
                // We do not want to fail when we cannot read from the cache. Report error and continue
                traceWarning(this._pageContext, WITCommonConstants.WorkItemTrackingArea, "IndexDbManager.TryGetDataFromCache", reason);

                return null;
            })
            .then(data => {
                if (!data) {
                    return null;
                }

                return data;
            });
    }

    private async _tryGetDataFromCache<T>(scopeId: string, type: string, key: string, stamp: string): Promise<T | null> {
        await this.getCacheProvider();

        if (!this._cacheProvider || !stamp) {
            return Promise.resolve(null);
        }

        return this._cacheProvider.read<T>(scopeId, type).then(cacheData => {
            if (!cacheData || !cacheData.data[key]) {
                // Nothing was cached for the given type, or the required key was not cached
                this._recordTelemetry(scopeId, type, key, stamp, "CacheMiss");

                return null;
            }

            if (cacheData.cachestamp !== stamp) {
                // Cached data is stale, cannot use it, clear from cache
                this._recordTelemetry(scopeId, type, key, stamp, "ClearedCachedStaleData");

                return this._cacheProvider!.delete(scopeId, type).then();
            }

            // this line generates A LOT of telemetry (megabytes) when we work with work items in batches
            // commenting and not removing so we can easily re-enable if needed
            // this._recordTelemetry(scopeId, type, key, stamp, "CacheHit", null);

            return cacheData.data[key];
        });
    }

    public async cacheData<T>(scopeId: string, type: string, key: string, data: T, cacheStamp: string | null): Promise<string[] | null> {
        await this.getCacheProvider();

        if (!this._cacheProvider || !cacheStamp) {
            // If caching is disabled or no stamp is provided, do not try to cache
            return Promise.resolve(null);
        }

        key = this._transformKey(key);
        let cachedKeys: string[];

        return this._cacheProvider
            .update<T>(scopeId, type, cacheData => {
                if (!cacheData || cacheData.cachestamp !== cacheStamp) {
                    // Either no data had been cached, or the cache stamp doesn't match, create new entry
                    cacheData = {
                        cachedAt: Date.now(),
                        cachestamp: cacheStamp,
                        data: {
                            [key]: data
                        }
                    };
                } else {
                    cacheData = {
                        ...cacheData,
                        data: {
                            ...cacheData.data,
                            [key]: data
                        }
                    };
                }

                // Save keys for cookie use
                cachedKeys = Object.keys(cacheData.data);

                return cacheData;
            })
            .then(() => {
                return cachedKeys;
            });
    }

    public async deleteData(scopeId: string, type: string): Promise<void> {
        await this.getCacheProvider();

        return this._cacheProvider!.delete(scopeId, type);
    }

    private async getCacheProvider(): Promise<ICacheProvider> {
        if (this._cacheProvider === undefined) {
            this._cacheProvider = await constructCacheProvider(this._pageContext);
        }

        return this._cacheProvider;
    }

    private _transformKey(key: string): string {
        return key.toLowerCase();
    }

    private _recordTelemetry(scopeId: string, type: string, key: string, stamp: string, message: string, cachedData?: any) {
        publishEvent(this._pageContext, "WIT", WorkItemDataTelemetryFeature, {
            scopeId,
            type,
            key,
            stamp,
            message,
            cachedData
        });
    }
}
